import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getChapters, getManhuaById, manhuaCover } from '../services/api';
import LegalSourcesList from '../components/LegalSourcesList';
import { trackEvent } from '../services/analytics';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

// MangaDex translatedLanguage codes the reader offers. Labels are what the
// picker shows; codes are what the /chapters endpoint expects.
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es-la', label: 'Español (LatAm)' },
  { code: 'es', label: 'Español' },
  { code: 'pt-br', label: 'Português (BR)' },
  { code: 'fr', label: 'Français' },
  { code: 'id', label: 'Indonesia' },
  { code: 'ko', label: '한국어' },
  { code: 'ja', label: '日本語' },
  { code: 'zh', label: '中文' },
  { code: 'ru', label: 'Русский' },
];

// Geo/browser default: map the visitor's locale onto a MangaDex code we offer,
// falling back to English. e.g. navigator.language "pt-BR" -> "pt-br", "es-MX" -> "es-la".
const defaultLanguage = () => {
  const nav = (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en').toLowerCase();
  if (nav.startsWith('pt')) return 'pt-br';
  if (nav.startsWith('es')) return nav === 'es-es' ? 'es' : 'es-la';
  const base = nav.split('-')[0];
  const hit = LANGUAGES.find((l) => l.code === base);
  return hit ? hit.code : 'en';
};

// Compact rectangular chapter tile — laid out in a responsive grid so chapters
// read left-to-right as "Ch 1, Ch 2, …" blocks rather than tall full-width rows.
const ChapterTile = ({ mangaId, chapter }) => {
  const isExternal = !!chapter.external_url;
  const num = chapter.chapter_number ? `Ch ${chapter.chapter_number}` : 'Oneshot';
  const tip = [
    chapter.title,
    chapter.scanlation_group && `📚 ${chapter.scanlation_group}`,
    chapter.publish_at && formatDate(chapter.publish_at),
    chapter.pages > 0 && `${chapter.pages}p`,
    isExternal && 'External — opens scanlator site',
  ].filter(Boolean).join(' · ');

  const inner = (
    <div
      title={tip}
      className="relative h-14 px-3 flex flex-col justify-center rounded-lg bg-zinc-900 border border-zinc-800 hover:border-rose-500/60 hover:bg-zinc-800/70 transition-colors"
    >
      <span className="text-rose-300 font-semibold text-sm leading-none">{num}</span>
      {chapter.title && (
        <span className="text-[11px] text-zinc-500 truncate mt-1">{chapter.title}</span>
      )}
      {isExternal && (
        <span className="absolute top-1 right-1.5 text-amber-400 text-xs" title="External">↗</span>
      )}
    </div>
  );

  if (isExternal) {
    return (
      <a href={chapter.external_url} target="_blank" rel="noopener noreferrer" className="block">
        {inner}
      </a>
    );
  }
  return (
    <Link to={`/manhua/${mangaId}/read/${chapter.chapter_id}`} className="block">
      {inner}
    </Link>
  );
};

const ManhuaDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [manhua, setManhua] = useState(null);
  const [chapters, setChapters] = useState(null);
  const [chaptersMeta, setChaptersMeta] = useState(null);
  const [order, setOrder] = useState('desc'); // newest first by default
  const [language, setLanguage] = useState(defaultLanguage); // geo/browser default
  const [summaryOpen, setSummaryOpen] = useState(false); // collapsible synopsis
  const [loading, setLoading] = useState(true);
  const [chaptersLoading, setChaptersLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getManhuaById(id)
      .then((m) => { if (!cancelled) { setManhua(m); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Could not load manhua'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setChaptersLoading(true);
    getChapters(id, { order, language, limit: 500 })
      .then((data) => {
        if (!cancelled) {
          setChapters(data.chapters);
          setChaptersMeta({
            total: data.total,
            cached: data.cached,
            last_synced_at: data.last_synced_at,
          });
          setChaptersLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setChapters([]);
          setChaptersMeta(null);
          setError(err?.response?.data?.detail || 'Failed to load chapters');
          setChaptersLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [id, order, language]);

  // Sort the grid reliably by numeric chapter number. MangaDex's feed order is
  // by readable-date, which looks jumbled when several scanlation groups post
  // overlapping numbers — so we re-sort client-side into clean Ch 1, 2, 3 order
  // (or reversed) regardless of how the API returned them. Non-numeric (oneshot,
  // "extra") sink to the end.
  const sortedChapters = useMemo(() => {
    if (!chapters) return chapters;
    const num = (c) => {
      const n = parseFloat(c.chapter_number);
      return Number.isFinite(n) ? n : null;
    };
    return [...chapters].sort((a, b) => {
      const na = num(a);
      const nb = num(b);
      if (na === null && nb === null) return 0;
      if (na === null) return 1; // unnumbered -> bottom
      if (nb === null) return -1;
      return order === 'asc' ? na - nb : nb - na;
    });
  }, [chapters, order]);

  // Languages THIS title actually has on MangaDex, intersected with the ones we
  // can label/offer. Drives the picker so it only shows real options.
  const availableLangs = useMemo(() => {
    const have = manhua?.available_translated_languages || [];
    const list = LANGUAGES.filter((l) => have.includes(l.code));
    return list.length ? list : [{ code: 'en', label: 'English' }];
  }, [manhua]);

  // Once the title loads, if the geo/browser default isn't offered here, snap to
  // the first language that is — so we never request a language with 0 chapters.
  useEffect(() => {
    if (!manhua) return;
    if (!availableLangs.some((l) => l.code === language)) {
      setLanguage(availableLangs[0].code);
    }
  }, [manhua, availableLangs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-rose-400">
        Loading...
      </div>
    );
  }

  if (!manhua) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-300 px-4 py-16 text-center">
        <p>Couldn't find that title.</p>
        <Link to="/" className="text-rose-400 hover:underline mt-4 inline-block">← Back to search</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Top bar */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-30 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/manhua'))}
            className="text-sm text-zinc-300 hover:text-rose-400 transition-colors"
          >
            ← Back
          </button>
          <Link to="/" className="text-sm text-zinc-400 hover:text-rose-400">What to Binge · Manhua</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-8 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
          <div className="aspect-[2/3] bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800">
            {manhua.poster_path ? (
              <img src={manhuaCover(manhua.poster_path, 512)} alt={manhua.title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-500">No cover</div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-widest text-rose-400 font-bold">
                <span>{manhua.content_kind}</span>
                {manhua.year && <span className="text-zinc-500">· {manhua.year}</span>}
                {manhua.status && (
                  <span className="px-2 py-0.5 bg-zinc-800 rounded-full text-zinc-300 normal-case tracking-normal">
                    {manhua.status}
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold leading-tight">{manhua.title}</h1>
              {manhua.alt_titles?.length > 0 && (
                <p className="text-xs text-zinc-500 mt-1 italic">
                  {manhua.alt_titles.slice(0, 2).join(' · ')}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-3 text-sm">
              {manhua.vote_average > 0 && (
                <div className="flex items-center gap-1 text-yellow-400">
                  ⭐ <span className="font-semibold">{manhua.vote_average.toFixed(1)}</span>
                  <span className="text-zinc-500 text-xs ml-1">/10</span>
                </div>
              )}
              {manhua.vote_count > 0 && (
                <div className="text-zinc-400 text-xs">{manhua.vote_count.toLocaleString()} tracking</div>
              )}
            </div>

            {manhua.overview && (
              <div className="max-w-3xl">
                <p
                  className={`text-sm text-zinc-300 leading-relaxed whitespace-pre-line transition-all ${
                    summaryOpen ? '' : 'line-clamp-4'
                  }`}
                >
                  {manhua.overview}
                </p>
                {manhua.overview.length > 280 && (
                  <button
                    onClick={() => setSummaryOpen((v) => !v)}
                    className="mt-1 text-xs font-semibold text-rose-400 hover:text-rose-300"
                  >
                    {summaryOpen ? 'Show less ▲' : 'Show more ▼'}
                  </button>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-1.5 pt-1">
              {manhua.genres?.map((g) => (
                <span key={`g-${g}`} className="text-xs px-2 py-0.5 bg-rose-500/15 text-rose-300 rounded-full border border-rose-500/30">{g}</span>
              ))}
              {manhua.themes?.map((t) => (
                <span key={`t-${t}`} className="text-xs px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded-full">{t}</span>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 pt-2 text-xs">
              {manhua.anilist_id && (
                <a href={`https://anilist.co/manga/${manhua.anilist_id}`} target="_blank" rel="noopener noreferrer"
                   className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-200">
                  AniList ↗
                </a>
              )}
              <a href={`https://mangadex.org/title/${manhua.id}`} target="_blank" rel="noopener noreferrer"
                 className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-200">
                MangaDex ↗
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">Chapters</h2>
            {chaptersMeta && (
              <p className="text-xs text-zinc-500 mt-1">
                {chaptersMeta.total} {(LANGUAGES.find((l) => l.code === language) || {}).label || 'English'} chapters
                {chaptersMeta.last_synced_at && ` · synced ${formatDate(chaptersMeta.last_synced_at)}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {availableLangs.length > 1 && (
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-rose-500/50"
                title="Chapter language"
              >
                {availableLangs.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            )}
            <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-1">
              <button
                onClick={() => setOrder('asc')}
                className={`px-3 py-1 text-xs rounded ${order === 'asc' ? 'bg-rose-500 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
              >
                Oldest
              </button>
              <button
                onClick={() => setOrder('desc')}
                className={`px-3 py-1 text-xs rounded ${order === 'desc' ? 'bg-rose-500 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
              >
                Newest
              </button>
            </div>
          </div>
        </div>

        {chaptersLoading && (
          <div className="text-zinc-500 text-sm py-8 text-center">Loading chapters...</div>
        )}

        {!chaptersLoading && chapters && chapters.length === 0 && (
          <div className="py-10 px-6 bg-gradient-to-b from-zinc-900 to-zinc-900/40 border border-zinc-800 rounded-xl">
            {manhua.legal_read_links?.length > 0 ? (
              <>
                <div className="text-center mb-5">
                  <div className="text-4xl mb-2">📚</div>
                  <h3 className="text-lg font-semibold">Read it officially</h3>
                  <p className="text-sm text-zinc-400 max-w-lg mx-auto mt-1">
                    This title is licensed in English — pick your favorite reader.
                    The publishers below pay the creators directly.
                  </p>
                </div>
                <div className="flex justify-center">
                  <LegalSourcesList links={manhua.legal_read_links} mode="prominent" />
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="text-4xl mb-3">🔖</div>
                <h3 className="text-lg font-semibold mb-2">Read it on another site</h3>
                <p className="text-sm text-zinc-400 max-w-md mx-auto mb-4">
                  We don't have chapters for this title here yet. Try another language
                  above, or continue on one of these readers:
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {manhua.anilist_id && (
                    <a href={`https://anilist.co/manga/${manhua.anilist_id}`} target="_blank" rel="noopener noreferrer"
                       onClick={() => trackEvent('External Read Clicked', { target: 'anilist', title: manhua.title })}
                       className="inline-block px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-sm rounded">
                      AniList ↗
                    </a>
                  )}
                  <a href={`https://www.mangaupdates.com/search.html?search=${encodeURIComponent(manhua.title)}`}
                     target="_blank" rel="noopener noreferrer"
                     onClick={() => trackEvent('External Read Clicked', { target: 'mangaupdates', title: manhua.title })}
                     className="inline-block px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-sm rounded">
                    MangaUpdates ↗
                  </a>
                  <a href={`https://www.google.com/search?q=${encodeURIComponent(`${manhua.title} read online`)}`}
                     target="_blank" rel="noopener noreferrer"
                     onClick={() => trackEvent('External Read Clicked', { target: 'web_search', title: manhua.title })}
                     className="inline-block px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-sm rounded">
                    Find other readers ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {!chaptersLoading && sortedChapters && sortedChapters.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {sortedChapters.map((c) => (
                <ChapterTile key={c.chapter_id} mangaId={id} chapter={c} />
              ))}
            </div>
            {manhua.legal_read_links?.length > 0 && (
              <LegalSourcesList
                links={manhua.legal_read_links}
                mode="compact"
                heading="Also available officially on"
              />
            )}
          </>
        )}

        {error && !chaptersLoading && (
          <div className="text-amber-400 text-sm mt-4">{error}</div>
        )}
      </section>
    </div>
  );
};

export default ManhuaDetailsPage;
